import type {
  PublicProfileContactChannelId,
  PublicProfileSocialId,
} from "@tabbio/types";

/**
 * Field contracts for the public-profile "Add social link" / "Add contact"
 * editors.
 *
 * The editors used to be free-text: a phone field happily accepted "dsadsa",
 * Slack accepted anything, and every social row demanded that the user type a
 * whole `https://…` URL by hand. This module gives each channel a *kind* plus
 * a canonical URL base, so a row can render a fixed prefix ("linkedin.com/in/")
 * and ask only for the part that actually varies - while still accepting a
 * pasted full URL and normalizing it back down to the handle.
 *
 * Two directions, both lossless for values that were stored before this
 * existed:
 * - `toEditablePublicProfileLink` - stored value → what the user edits.
 * - `toStoredPublicProfileLink`   - what the user typed → stored value.
 */

export type PublicProfileLinkMode = "individual" | "company";

export type PublicProfileLinkKind =
  | "handle"
  | "url"
  | "phone"
  | "email"
  | "text";

export interface PublicProfileLinkField {
  kind: PublicProfileLinkKind;
  /**
   * Non-editable text rendered before the caret ("linkedin.com/in/"). Null for
   * kinds where the whole value is the input (phone, email, free URL).
   */
  prefix: string | null;
  /** Example of the editable part; the editors use it as the placeholder. */
  example: string;
  /**
   * Host+path bases accepted when the user pastes a full URL. `bases[0]` is
   * canonical and is the one used to rebuild the stored URL.
   */
  bases: readonly string[];
  /** Registered domains accepted for `kind: "url"` fields (subdomains included). */
  hosts: readonly string[];
}

const HANDLE_PATTERN = /^[A-Za-z0-9._-]{1,64}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

// A leading URL scheme like "https:", "javascript:", "mailto:". Deliberately
// does NOT match "linkedin.com/in/acme" (any colon there follows a slash), so
// scheme-less input can be upgraded to https.
const SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;

function handleField(
  bases: readonly string[],
  example: string,
): PublicProfileLinkField {
  return { kind: "handle", prefix: bases[0], example, bases, hosts: [] };
}

function urlField(
  hosts: readonly string[],
  example: string,
): PublicProfileLinkField {
  return { kind: "url", prefix: null, example, bases: [], hosts };
}

const PHONE_FIELD: PublicProfileLinkField = {
  kind: "phone",
  prefix: null,
  example: "+971 50 000 0000",
  bases: [],
  hosts: [],
};

const EMAIL_FIELD: PublicProfileLinkField = {
  kind: "email",
  prefix: null,
  example: "you@company.com",
  bases: [],
  hosts: [],
};

const TEXT_FIELD: PublicProfileLinkField = {
  kind: "text",
  prefix: null,
  example: "",
  bases: [],
  hosts: [],
};

/**
 * WhatsApp is a phone number everywhere it is shown, but it is *stored* as a
 * wa.me link so the existing renderers (which only prepend `https://`) keep
 * producing a working CTA. Modelling it as a handle whose "handle" is the
 * digit string gives both without a special case at every call site.
 */
const WHATSAPP_FIELD: PublicProfileLinkField = {
  kind: "phone",
  prefix: null,
  example: "+971 50 000 0000",
  bases: ["wa.me/"],
  hosts: [],
};

const SOCIAL_FIELDS: Record<PublicProfileSocialId, PublicProfileLinkField> = {
  linkedin: handleField(
    ["linkedin.com/in/", "linkedin.com/pub/", "linkedin.com/company/"],
    "username",
  ),
  instagram: handleField(["instagram.com/", "instagr.am/"], "username"),
  x: handleField(["x.com/", "twitter.com/"], "username"),
  tiktok: handleField(["tiktok.com/@", "tiktok.com/"], "username"),
  youtube: handleField(
    ["youtube.com/@", "youtube.com/", "youtu.be/"],
    "channel",
  ),
  snapchat: handleField(["snapchat.com/add/", "snapchat.com/"], "username"),
  facebook: handleField(
    ["facebook.com/", "fb.com/", "fb.me/"],
    "page",
  ),
  discord: handleField(["discord.gg/", "discord.com/invite/"], "invite"),
  pinterest: handleField(["pinterest.com/"], "username"),
  spotify: handleField(
    ["open.spotify.com/", "spotify.com/"],
    "artist/id",
  ),
  github: handleField(["github.com/"], "username"),
  behance: handleField(["behance.net/"], "username"),
  dribbble: handleField(["dribbble.com/"], "username"),
  tabbio: handleField(["tabbio.com/"], "handle"),
  whatsapp: WHATSAPP_FIELD,
  email: EMAIL_FIELD,
};

/**
 * The company's own website or careers page.
 *
 * It is not a social platform - it has no host to lock to and no handle to
 * strip - so it is a plain URL field. It lives here because the Add link sheet
 * edits it alongside the social rows and needs the same parse/commit contract.
 */
export const PUBLIC_PROFILE_WEBSITE_FIELD: PublicProfileLinkField = urlField(
  [],
  "https://www.example.com",
);

/** LinkedIn is the one platform whose canonical path differs for a company. */
const LINKEDIN_COMPANY_FIELD = handleField(
  ["linkedin.com/company/", "linkedin.com/school/", "linkedin.com/in/"],
  "company",
);

const CONTACT_FIELDS: Record<
  PublicProfileContactChannelId,
  PublicProfileLinkField
> = {
  tabbioMessage: TEXT_FIELD,
  whatsapp: WHATSAPP_FIELD,
  telegram: handleField(
    ["t.me/", "telegram.me/", "telegram.dog/"],
    "username",
  ),
  // Slack has no single public profile shape (workspace, invite, and app links
  // all differ), so it stays a URL field - but one locked to slack.com.
  slack: urlField(["slack.com"], "https://acme.slack.com"),
  email: EMAIL_FIELD,
  phone: PHONE_FIELD,
  landline: {
    ...PHONE_FIELD,
    example: "+971 2 000 0000",
  },
};

export function getPublicProfileSocialField(
  id: PublicProfileSocialId,
  mode: PublicProfileLinkMode = "individual",
): PublicProfileLinkField {
  if (id === "linkedin" && mode === "company") return LINKEDIN_COMPANY_FIELD;
  return SOCIAL_FIELDS[id] ?? TEXT_FIELD;
}

export function getPublicProfileContactField(
  id: PublicProfileContactChannelId,
): PublicProfileLinkField {
  return CONTACT_FIELDS[id] ?? TEXT_FIELD;
}

function stripScheme(value: string) {
  const withoutScheme = value.replace(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//, "");
  // Only shed a host prefix when what follows still looks like a host, so a
  // handle that happens to start "m." (`m.hassan`) is left alone.
  const withoutHostPrefix = withoutScheme.replace(/^(www|m)\./i, "");
  return withoutHostPrefix.includes(".") ? withoutHostPrefix : withoutScheme;
}

function hostMatches(hostname: string, allowed: readonly string[]) {
  return allowed.some(
    (domain) => hostname === domain || hostname.endsWith(`.${domain}`),
  );
}

/**
 * Keep only what can appear in a dialable number: digits, a single leading
 * `+`, and the separators people type out of habit. This is what stops a phone
 * field from accepting "dsadsa".
 */
export function sanitizePhoneInput(raw: string): string {
  const cleaned = raw.replace(/[^\d+\s().-]/g, "");
  const plus = cleaned.trimStart().startsWith("+");
  return `${plus ? "+" : ""}${cleaned.replace(/\+/g, "")}`.replace(
    /\s{2,}/g,
    " ",
  );
}

/** Digits only, `+` dropped - the form wa.me and `tel:` want. */
export function toPhoneDigits(raw: string): string {
  return raw.replace(/\D/g, "");
}

export function isValidPhoneValue(raw: string): boolean {
  const digits = toPhoneDigits(raw);
  return digits.length >= 6 && digits.length <= 15;
}

export function isValidEmailValue(raw: string): boolean {
  return EMAIL_PATTERN.test(raw.trim());
}

/**
 * Validate and normalize a scheme-less-or-https URL against a host allowlist.
 * Fails closed: `http:`, `javascript:`, credentials, and off-allowlist hosts
 * all return null rather than being silently repaired.
 */
export function normalizeAllowlistedUrl(
  raw: string,
  hosts: readonly string[],
): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const candidate = SCHEME_PATTERN.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return null;
  }

  if (parsed.protocol !== "https:") return null;
  if (parsed.username || parsed.password) return null;
  if (hosts.length > 0 && !hostMatches(parsed.hostname.toLowerCase(), hosts)) {
    return null;
  }

  return parsed.toString();
}

/**
 * Whether input that matched no base should still be treated as a link rather
 * than a handle. A dot is deliberately NOT enough: "john.doe" is a perfectly
 * ordinary Instagram handle, and reading it as a hostname would store
 * `https://john.doe/`. Only an explicit scheme or a path segment qualifies.
 */
function looksLikeForeignUrl(trimmed: string, bare: string) {
  return SCHEME_PATTERN.test(trimmed) || bare.includes("/");
}

function matchBase(field: PublicProfileLinkField, bare: string) {
  const lower = bare.toLowerCase();
  for (const base of field.bases) {
    if (lower.startsWith(base.toLowerCase())) {
      return { base, rest: bare.slice(base.length) };
    }
  }
  return null;
}

/**
 * A stored link split into the fixed part the editor renders as a chip and the
 * part the user actually types.
 */
export interface PublicProfileLinkParts {
  /** Prefix to render before the caret, or null when the whole value is typed. */
  prefix: string | null;
  /** The editable remainder. */
  value: string;
}

/**
 * Stored value → what the editor shows.
 *
 * The prefix comes from the base the stored URL actually matched, NOT from the
 * field's canonical base. That distinction matters: `youtube.com/channel/UC123`
 * and `linkedin.com/pub/ahmed` are different identity spaces from
 * `youtube.com/@handle` and `linkedin.com/in/handle`, so showing the canonical
 * prefix beside them would misdescribe the link and rebuilding through it would
 * point somewhere else entirely. A value the field does not recognise gets no
 * prefix and is shown whole, so the user can see and fix exactly what is
 * stored.
 */
export function splitPublicProfileLink(
  field: PublicProfileLinkField,
  stored: string | null | undefined,
): PublicProfileLinkParts {
  const trimmed = (stored ?? "").trim();

  if (field.kind === "text" || field.kind === "url") {
    return { prefix: null, value: trimmed };
  }

  if (field.kind === "email") {
    return {
      prefix: null,
      value: trimmed.startsWith("mailto:")
        ? trimmed.slice("mailto:".length)
        : trimmed,
    };
  }

  if (field.kind === "phone") {
    if (!trimmed) return { prefix: null, value: "" };
    const withoutTel = trimmed.startsWith("tel:")
      ? trimmed.slice("tel:".length)
      : trimmed;
    const matched = matchBase(field, stripScheme(withoutTel));
    if (matched) {
      const digits = toPhoneDigits(matched.rest);
      return { prefix: null, value: digits ? `+${digits}` : "" };
    }
    return { prefix: null, value: sanitizePhoneInput(withoutTel) };
  }

  const canonical = field.bases[0] ?? null;
  if (!trimmed) return { prefix: canonical, value: "" };

  const bare = stripScheme(trimmed);
  const matched = matchBase(field, bare);
  if (matched) {
    return { prefix: matched.base, value: matched.rest.replace(/\/+$/, "") };
  }

  // A URL on some other host: no prefix applies, so show it whole.
  if (looksLikeForeignUrl(trimmed, bare)) return { prefix: null, value: trimmed };

  return { prefix: canonical, value: bare.replace(/^@/, "") };
}

/** Convenience wrapper for callers that only need the editable remainder. */
export function toEditablePublicProfileLink(
  field: PublicProfileLinkField,
  stored: string | null | undefined,
): string {
  return splitPublicProfileLink(field, stored).value;
}

/** A single path segment with no separators: "ahmed", "john.doe", "acme-hq". */
function isBareHandle(value: string) {
  return HANDLE_PATTERN.test(value);
}

/**
 * What the user typed → the value persisted on the profile.
 *
 * `prefix` is whatever the editor was showing beside the input (from
 * `splitPublicProfileLink`); pass null for fields with no prefix. A value that
 * carries its own scheme or a recognised base replaces the prefix entirely, so
 * pasting a full URL over a handle field does the obvious thing.
 *
 * Returns `""` when the input cannot be turned into something openable; callers
 * treat that as "not committable yet" and keep the previous stored value.
 */
export function toStoredPublicProfileLink(
  field: PublicProfileLinkField,
  input: string,
  prefix?: string | null,
): string {
  const trimmed = input.trim();
  if (!trimmed) return "";

  if (field.kind === "text") return trimmed;

  if (field.kind === "email") {
    const email = trimmed.startsWith("mailto:")
      ? trimmed.slice("mailto:".length)
      : trimmed;
    return isValidEmailValue(email) ? email : "";
  }

  if (field.kind === "phone") {
    if (!isValidPhoneValue(trimmed)) return "";
    const digits = toPhoneDigits(trimmed);
    // WhatsApp round-trips as a wa.me link; a plain phone stays a phone.
    return field.bases.length > 0
      ? `https://${field.bases[0]}${digits}`
      : `+${digits}`;
  }

  if (field.kind === "url") {
    return normalizeAllowlistedUrl(trimmed, field.hosts) ?? "";
  }

  // Handle field. A pasted URL is re-split so its own base wins over whatever
  // prefix the row was showing; nothing about the path is ever rewritten.
  const bare = stripScheme(trimmed);
  if (SCHEME_PATTERN.test(trimmed) || matchBase(field, bare)) {
    const parts = splitPublicProfileLink(field, trimmed);
    const composed = parts.prefix
      ? `${parts.prefix}${parts.value}`
      : parts.value;
    return normalizeAllowlistedUrl(composed, []) ?? "";
  }

  const handle = bare.replace(/^@/, "").replace(/^\/+/, "").replace(/\/+$/, "");
  if (!handle) return "";

  const base = prefix ?? field.bases[0];
  if (!base) return normalizeAllowlistedUrl(handle, []) ?? "";
  return normalizeAllowlistedUrl(`${base}${handle}`, []) ?? "";
}

/**
 * Whether the editable text is acceptable. Empty is `false` - the editors treat
 * "nothing typed yet" separately from "typed something wrong".
 */
export function isPublicProfileLinkInputValid(
  field: PublicProfileLinkField,
  input: string,
  prefix?: string | null,
): boolean {
  const trimmed = input.trim();
  if (!trimmed) return false;

  switch (field.kind) {
    case "text":
      return true;
    case "phone":
      return isValidPhoneValue(trimmed);
    case "email":
      return isValidEmailValue(trimmed);
    case "url":
      return normalizeAllowlistedUrl(trimmed, field.hosts) !== null;
    case "handle": {
      // No link, at any shape, contains whitespace. Without this the URL parse
      // below would percent-encode a space and call it valid.
      if (/\s/.test(trimmed)) return false;
      const bare = stripScheme(trimmed);
      const isLink = SCHEME_PATTERN.test(trimmed) || bare.includes("/");
      // A bare handle is held to the tighter character set, which is what
      // rejects "@@@" or "a  b" before it can be pasted into a URL path. Once
      // there is a path, the URL parse below is the only judge.
      if (!isLink && !isBareHandle(bare.replace(/^@/, ""))) return false;
      return toStoredPublicProfileLink(field, trimmed, prefix) !== "";
    }
    default:
      return true;
  }
}

/**
 * Per-keystroke filter. Phone fields drop letters as they are typed - the
 * cheapest way to make "number is accepting alphabets" impossible rather than
 * merely reported after the fact. Handle/email fields only lose whitespace, so
 * pasting a full URL still works and is normalized on commit.
 */
export function filterPublicProfileLinkInput(
  field: PublicProfileLinkField,
  input: string,
): string {
  if (field.kind === "phone") return sanitizePhoneInput(input);
  if (field.kind === "email" || field.kind === "handle") {
    return input.replace(/\s+/g, "");
  }
  return input;
}
